import React, { useState, useMemo, useCallback } from 'react';
import { Box, Button, IconButton, Grid, TextField, MenuItem, InputAdornment, Chip, Select, FormControl, InputLabel, FormHelperText, Autocomplete } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import Replay from '@mui/icons-material/Replay';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { generateFieldOptions, buildFieldNameVariants } from '../../../utils/dynamicModalConfig';

const getDynFieldLabel = (field) => {
    const lbl = field.label || field.name || '';
    if (field.dynamic_field && lbl.includes('.')) return lbl.split('.').pop();
    return lbl;
};

const ResetIconButton = ({ fieldName, originalValue, currentValue, onReset, isMultiSelect, explicitChanged }) => {
    let hasChanged = false;

    if (explicitChanged !== undefined) {
        hasChanged = explicitChanged;
    } else {
        const isDateField = /date/i.test(fieldName) || /dob/i.test(fieldName);
        if (isDateField) {
            const originalDate = moment(originalValue).isValid()
                ? moment(originalValue).format('YYYY-MM-DD')
                : originalValue;
            const currentDate = moment(currentValue).isValid()
                ? moment(currentValue).format('YYYY-MM-DD')
                : currentValue;

            if ((!originalValue && !currentValue) || (originalValue === '' && currentValue === '')) {
                hasChanged = false;
            } else {
                hasChanged = originalDate !== currentDate;
            }
        } else {
            if (Array.isArray(originalValue) || Array.isArray(currentValue)) {
                const arr1 = Array.isArray(originalValue) ? originalValue : (originalValue ? [originalValue] : []);
                const arr2 = Array.isArray(currentValue) ? currentValue : (currentValue ? [currentValue] : []);

                const s1 = arr1.map(v => String(v ?? '')).sort().join(',');
                const s2 = arr2.map(v => String(v ?? '')).sort().join(',');
                hasChanged = s1 !== s2;
            } else {
                hasChanged = String(originalValue ?? '') !== String(currentValue ?? '');
            }
        }
    }

    if (!hasChanged) return null;

    const btn = (
        <IconButton
            size="small"
            onClick={(e) => {
                e.stopPropagation();
                onReset(fieldName);
            }}
            onMouseDown={(e) => e.preventDefault()}
            tabIndex={-1}
        >
            <Replay fontSize="small" />
        </IconButton>
    );

    if (isMultiSelect) {
        return <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>{btn}</Box>;
    }

    return (
        <InputAdornment position="end">
            {btn}
        </InputAdornment>
    );
};

const DetailRenderer = (props) => {
    const {
        data: dataRecord,
        fields,
        onRowChange,
        getLatestMergedDropdownData,
        getLatestErrors,
        readOnlyDialog,
        getFieldValue,
        getOriginalValue,
        getDisplayValue,
        columns = 3,
        getLatestMultiRowData
    } = props;

    const errors = getLatestErrors() || {};
    const multiRowData = getLatestMultiRowData() || [];
    const mergedDropdownData = getLatestMergedDropdownData() || {};

    const record = multiRowData.find(r => r.id === dataRecord.id) || dataRecord;

    const rowIndex = React.useMemo(() => {
        // First try: match by id (safe even when id === 0 or falsy non-null)
        const byId = multiRowData.findIndex(r =>
            r.id !== undefined && r.id !== null && r.id === record.id
        );
        if (byId !== -1) return byId;
        // Fallback: reference equality
        const byRef = multiRowData.findIndex(r => r === record);
        if (byRef !== -1) return byRef;
        // Last resort: match all non-private fields (handles cloned records)
        return multiRowData.findIndex(r =>
            Object.keys(record).filter(k => !k.startsWith('_')).every(k => r[k] === record[k])
        );
    }, [multiRowData, record]);

    if (rowIndex === -1) return null;

    const handleReset = (fieldName) => {
        let val = getOriginalValue(record, { name: fieldName });
        const field = fields.find(f => f.name === fieldName);

        // Ensure date fields are restored in YYYY-MM-DD format for TextField
        if (field?.type === 'date' && val) {
            const parsed = moment(val, ['MM/DD/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY', 'MM-DD-YYYY']);
            if (parsed.isValid()) {
                val = parsed.format('YYYY-MM-DD');
            }
        }
        onRowChange(record.id, fieldName, val);
    };
    const isIncomplete = fields.some(f => f.required && !getFieldValue(record, f));

    return (
        <Box sx={{ p: 3, backgroundColor: '#fcfcfc', borderBottom: '1px solid #eee' }}>
            {isIncomplete && (
                <Box sx={{ mb: 2 }}>
                    <Chip
                        label="Incomplete"
                        color="error"
                        variant="outlined"
                        size="small"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                    />
                </Box>
            )}
            <Grid container spacing={2.5}>
                {fields.filter(field => !field.isHidden).map((field) => {
                    const fieldValue = getFieldValue(record, field);
                    const originalValue = getOriginalValue(record, field);
                    const fieldError = errors[`${record.id}_${field.name}`];

                    let hasChanged = false;
                    if (Array.isArray(fieldValue) || Array.isArray(originalValue)) {
                        const s1 = (Array.isArray(originalValue) ? originalValue : (originalValue ? String(originalValue).split(',').filter(Boolean) : [])).map(v => String(v ?? '').trim()).sort().join(',');
                        const s2 = (Array.isArray(fieldValue) ? fieldValue : (fieldValue ? String(fieldValue).split(',').filter(Boolean) : [])).map(v => String(v ?? '').trim()).sort().join(',');
                        hasChanged = s1 !== s2;
                    } else {
                        hasChanged = fieldValue !== originalValue;
                    }

                    const labelContent = (
                        <span style={{ fontSize: '0.85rem', fontWeight: 500, display: 'inline-block', width: '100%', lineHeight: '1.43em', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                            {getDynFieldLabel(field)}
                            {field.required && <span style={{ color: '#999', marginLeft: '4px' }}>*</span>}
                        </span>
                    );

                    const normalizeBoolToken = (val) => {
                        if (val === undefined || val === null) return null;
                        const s = String(val).trim().toLowerCase();
                        if (val === true || s === 'true' || s === '1' || s === 'y' || s === 'yes') return 'yes';
                        if (val === false || s === 'false' || s === '0' || s === 'n' || s === 'no') return 'no';
                        return null;
                    };

                    const normalizeSelectValue = (current, options) => {
                        if (current === undefined || current === null || current === '' || !Array.isArray(options) || options.length === 0) return current;
                        const token = String(current).trim().toLowerCase();
                        const boolToken = normalizeBoolToken(current);
                        const matched = options.find(o => {
                            const idVal = o.id ?? o.value;
                            const labelVal = o.label ?? o.name ?? o.description ?? o.code;
                            const idToken = idVal !== undefined && idVal !== null ? String(idVal).trim().toLowerCase() : '';
                            const labelToken = labelVal !== undefined && labelVal !== null ? String(labelVal).trim().toLowerCase() : '';
                            if (idToken === token || labelToken === token) return true;
                            if (!boolToken) return false;
                            const idBool = normalizeBoolToken(idVal);
                            const labelBool = normalizeBoolToken(labelVal);
                            return idBool === boolToken || labelBool === boolToken;
                        });
                        if (!matched) return current;
                        return matched.id ?? matched.value ?? current;
                    };

                    const options = (field.type === 'select' || field.type === 'multiSelect' || !field.type || field.type === 'text') 
                        ? (generateFieldOptions(field, mergedDropdownData) || []) 
                        : [];

                    if (options.length > 0 && field.type !== 'multiSelect') {
                        const normalizedValue = normalizeSelectValue(fieldValue, options);
                        return (
                            <Grid item xs={12} md={12 / columns} key={field.name} sx={{ minWidth: 0 }}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={labelContent}
                                    value={normalizedValue}
                                    onChange={(e) => onRowChange(record.id, field.name, e.target.value)}
                                    error={!!fieldError}
                                    helperText={fieldError || (originalValue !== undefined && originalValue !== null && originalValue !== '' ? `Original: ${getDisplayValue(originalValue, field, mergedDropdownData)}` : '')}
                                    disabled={readOnlyDialog}
                                    InputLabelProps={{
                                        style: {
                                            whiteSpace: 'normal',
                                            lineHeight: '1.2em',
                                            overflow: 'visible'
                                        }
                                    }}
                                    InputProps={{
                                        endAdornment: (
                                            <ResetIconButton
                                                fieldName={field.name}
                                                originalValue={originalValue}
                                                currentValue={fieldValue}
                                                onReset={handleReset}
                                                explicitChanged={hasChanged}
                                            />
                                        )
                                    }}
                                    sx={{
                                        '& .MuiFormLabel-asterisk': { display: 'none' },
                                        '& .MuiInputLabel-root': {
                                            whiteSpace: 'normal',
                                            overflow: 'visible',
                                            maxWidth: 'calc(133% - 32px)',
                                            textOverflow: 'unset'
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            maxWidth: 'calc(133% - 32px)'
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'white',
                                        },
                                        '& .MuiFormHelperText-root': {
                                            wordBreak: 'break-word',
                                            whiteSpace: 'normal',
                                            overflowWrap: 'break-word'
                                        }
                                    }}
                                >
                                    {options.map((opt) => (
                                        <MenuItem key={opt.id ?? opt.value} value={opt.id ?? opt.value}>
                                            {opt.label || opt.name || opt.value}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        );
                    }

                    if (field.type === 'multiSelect') {
                        const options = generateFieldOptions(field, mergedDropdownData) || [];
                        const selectedValues = Array.isArray(fieldValue) ? fieldValue : (fieldValue ? [fieldValue] : []);
                        const selectedOptions = options.filter(opt =>
                            selectedValues.some(val => String(val) === String(opt.id ?? opt.value))
                        );

                        return (
                            <Grid item xs={12} md={12 / columns} key={field.name} sx={{ minWidth: 0 }}>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={options}
                                    value={selectedOptions}
                                    getOptionLabel={(option) => String(option.label || option.name || option.value || '')}
                                    isOptionEqualToValue={(o, v) => String(o.id ?? o.value) === String(v.id ?? v.value)}
                                    onChange={(e, newValue) => onRowChange(record.id, field.name, newValue.map(v => v.id ?? v.value))}
                                    disabled={readOnlyDialog}
                                    disableCloseOnSelect
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={option.id ?? option.value}
                                                label={option.label || option.name || option.value}
                                                size="small"
                                                sx={{ height: 24, fontSize: '0.75rem', backgroundColor: '#f2f2f2' }}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label={labelContent}
                                            fullWidth
                                            error={!!fieldError}
                                            helperText={fieldError || (originalValue !== undefined && originalValue !== null && originalValue !== '' ? `Original: ${getDisplayValue(originalValue, field, mergedDropdownData)}` : '')}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            top: 6,
                                                            right: 9,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.25,
                                                            zIndex: 2,
                                                            '& .MuiAutocomplete-endAdornment': {
                                                                position: 'static !important',
                                                                transform: 'none !important',
                                                                top: 'auto !important',
                                                                right: 'auto !important',
                                                                display: 'flex',
                                                                alignItems: 'center'
                                                            },
                                                            '& .MuiInputAdornment-root': {
                                                                margin: 0,
                                                                maxHeight: 'none',
                                                                height: 'auto'
                                                            }
                                                        }}
                                                    >
                                                        {params.InputProps.endAdornment}
                                                        <ResetIconButton
                                                            fieldName={field.name}
                                                            originalValue={originalValue}
                                                            currentValue={fieldValue}
                                                            onReset={handleReset}
                                                            explicitChanged={hasChanged}
                                                        />
                                                    </Box>
                                                ),
                                            }}
                                            sx={{
                                                '& .MuiFormLabel-asterisk': { display: 'none' },
                                                '& .MuiInputLabel-root': {
                                                    whiteSpace: 'normal',
                                                    overflow: 'visible',
                                                    maxWidth: 'calc(133% - 32px)',
                                                    textOverflow: 'unset'
                                                },
                                                '& .MuiInputLabel-shrink': {
                                                    maxWidth: 'calc(133% - 32px)'
                                                },
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: 'white',
                                                    height: 'auto',
                                                    minHeight: '40px',
                                                    flexWrap: 'wrap',
                                                    alignItems: 'flex-start',
                                                    paddingTop: '6px',
                                                    paddingBottom: '6px',
                                                    paddingRight: '96px !important'
                                                },
                                                '& .MuiOutlinedInput-root .MuiAutocomplete-input': {
                                                    minWidth: '30px',
                                                    width: 'auto',
                                                },
                                                '& .MuiFormHelperText-root': {
                                                    wordBreak: 'break-word',
                                                    whiteSpace: 'normal',
                                                    overflowWrap: 'break-word'
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Grid>
                        );
                    }

                    if (field.type === 'checkbox' || field.type === 'boolean') {
                        return (
                            <Grid item xs={12} md={12 / columns} key={field.name} sx={{ minWidth: 0 }}>
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    label={labelContent}
                                    value={fieldValue === true || fieldValue === 'true' || fieldValue === 'Yes' ? 'Yes' : 'No'}
                                    onChange={(e) => onRowChange(record.id, field.name, e.target.value)}
                                    error={!!fieldError}
                                    helperText={fieldError || (originalValue !== undefined && originalValue !== null && originalValue !== '' ? `Original: ${getDisplayValue(originalValue, field, mergedDropdownData)}` : '')}
                                    disabled={readOnlyDialog}
                                    InputProps={{
                                        endAdornment: (
                                            <ResetIconButton
                                                fieldName={field.name}
                                                originalValue={originalValue}
                                                currentValue={fieldValue}
                                                onReset={handleReset}
                                                explicitChanged={hasChanged}
                                            />
                                        )
                                    }}
                                    InputLabelProps={{
                                        style: {
                                            whiteSpace: 'normal',
                                            lineHeight: '1.2em',
                                            overflow: 'visible'
                                        }
                                    }}
                                    sx={{
                                        '& .MuiFormLabel-asterisk': { display: 'none' },
                                        '& .MuiInputLabel-root': {
                                            whiteSpace: 'normal',
                                            overflow: 'visible',
                                            maxWidth: 'calc(133% - 32px)',
                                            textOverflow: 'unset'
                                        },
                                        '& .MuiInputLabel-shrink': {
                                            maxWidth: 'calc(133% - 32px)'
                                        },
                                        '& .MuiOutlinedInput-root': {
                                            backgroundColor: 'white',
                                        },
                                        '& .MuiFormHelperText-root': {
                                            wordBreak: 'break-word',
                                            whiteSpace: 'normal',
                                            overflowWrap: 'break-word'
                                        }
                                    }}
                                >
                                    <MenuItem value="Yes">Yes</MenuItem>
                                    <MenuItem value="No">No</MenuItem>
                                </TextField>
                            </Grid>
                        );
                    }

                    return (
                        <Grid item xs={12} md={12 / columns} key={field.name} sx={{ minWidth: 0 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label={labelContent}
                                value={fieldValue}
                                onChange={(e) => onRowChange(record.id, field.name, e.target.value)}
                                error={!!fieldError}
                                helperText={fieldError || (originalValue !== undefined && originalValue !== null && originalValue !== '' ? `Original: ${getDisplayValue(originalValue, field, mergedDropdownData)}` : '')}
                                disabled={readOnlyDialog}
                                type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
                                multiline={field.type === 'textarea'}
                                rows={field.type === 'textarea' ? 3 : 1}
                                InputLabelProps={{
                                    shrink: field.type === 'date' ? true : undefined,
                                    style: {
                                        whiteSpace: 'normal',
                                        lineHeight: '1.2em',
                                        overflow: 'visible'
                                    }
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <ResetIconButton
                                            fieldName={field.name}
                                            originalValue={originalValue}
                                            currentValue={fieldValue}
                                            onReset={handleReset}
                                            explicitChanged={hasChanged}
                                        />
                                    )
                                }}
                                sx={{
                                    '& .MuiFormLabel-asterisk': { display: 'none' },
                                    '& .MuiInputLabel-root': {
                                        whiteSpace: 'normal',
                                        overflow: 'visible',
                                        maxWidth: 'calc(133% - 32px)',
                                        textOverflow: 'unset'
                                    },
                                    '& .MuiInputLabel-shrink': {
                                        maxWidth: 'calc(133% - 32px)'
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'white',
                                    },
                                    '& .MuiFormHelperText-root': {
                                        wordBreak: 'break-word',
                                        whiteSpace: 'normal',
                                        overflowWrap: 'break-word'
                                    }
                                }}
                            />
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
    };

function MasterDynamicDialog({
    multiRowData, fields, onRowChange, onAddRow, onDeleteRow, mergedDropdownData, readOnlyDialog, actionLabels, errors = {}, allowAddRow, allowDelete, showSearch = true, title = '', searchText: externalSearchText, onSearchChange, headerSelectLabel = '', headerSelectValue = '', headerSelectOptions = [], onHeaderSelectChange, headerSelectField = '', columns = 3, tableHeaderFields = [], grouping = ""
}) {
    const [internalSearch, setInternalSearch] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('All');

    const searchText = externalSearchText !== undefined ? externalSearchText : internalSearch;
    const setSearchText = onSearchChange || setInternalSearch;

    const groupingField = useMemo(() => fields.find(f => f.name === grouping),
        [fields, grouping]);

    const groupingOptions = useMemo(() => {
        if (!grouping) return [];
        if (groupingField && (groupingField.type === 'select' || groupingField.type === 'multiSelect')) {
            return generateFieldOptions(groupingField, mergedDropdownData) || [];
        }
        const uniqueValues = [...new Set(multiRowData.map(item => item[grouping]).filter(v => v !== null && v !== undefined && v !== ''))];
        return uniqueValues.map(val => ({ label: String(val), value: String(val) }));
    }, [groupingField, mergedDropdownData, multiRowData, grouping]);

    const getDisplayValue = useCallback((value, field, dropdownData) => {
        if (value === undefined || value === null || value === '') return '';

        const isBoolLikeField = /wheelchair|primary.*location|isprimary|boardcertified|is.*active/i.test(field.name || '');
        const looksLikeBool = value === true || value === false || String(value).toLowerCase() === 'true' || String(value).toLowerCase() === 'false';
        
        if (isBoolLikeField || looksLikeBool) {
            const lowerVal = String(value).toLowerCase();
            if (lowerVal === 'true' || lowerVal === 'yes' || lowerVal === 'y' || lowerVal === '1' || value === 1 || value === true) return 'Yes';
            if (lowerVal === 'false' || lowerVal === 'no' || lowerVal === 'n' || lowerVal === '0' || value === 0 || value === false) return 'No';
        }

        const isSelect = (field.type === 'select' || field.type === 'multiSelect' || !field.type || field.type === 'text');
        const options = isSelect
            ? (generateFieldOptions(field, dropdownData) || [])
            : [];

        if (options.length > 0) {
            if (options && options.length > 0) {
                const match = options.find(o => String(o.id ?? o.value ?? '') === String(value));
                if (match) return match.label || match.name || match.value;
            }
            
            if (isSelect) {
                if (typeof value === 'string' && isNaN(Number(value))) return value;
                return value;
            }
        }

        if (field.type === 'multiSelect') {
            const options = generateFieldOptions(field, dropdownData) || [];
            const values = Array.isArray(value) ? value : (typeof value === 'string' ? value.split(',').map(v => v.trim()) : [value]);
            const labels = values.map(v => {
                const match = options.find(o => String(o.id ?? o.value ?? '') === String(v));
                return match ? (match.label || match.name || match.value) : v;
            });
            return labels.join(', ');
        }

        if (field.type === 'boolean' || field.type === 'checkbox') {
            const lowerVal = String(value).toLowerCase();
            return lowerVal === 'true' || lowerVal === 'yes' || lowerVal === 'y' || lowerVal === '1' || value === 1 || value === true ? 'Yes' : 'No';
        }

        return value;
    }, []);

    const getFieldValue = useCallback((record, field) => {
        if (!record || !field) return '';
        const variants = buildFieldNameVariants(field.name);
        for (const variant of variants) {
            if (record[variant] !== undefined && record[variant] !== null) {
                return record[variant];
            }
        }
        return '';
    }, []);

    const getOriginalValue = useCallback((record, field) => {
        if (!record || !field) return '';
        const variants = buildFieldNameVariants(field.name);

        // Check for original labels/data variants
        for (const variant of variants) {
            const labelKey = `_${variant}OriginalLabel`;
            const dataKey = `_${variant}OriginalData`;
            const rawKey = `_${variant}OriginalRaw`;
            if (record[labelKey] !== undefined) return record[labelKey];
            if (record[dataKey] !== undefined) return record[dataKey];
            if (record[rawKey] !== undefined) return record[rawKey];
        }

        // Fallback to current values
        for (const variant of variants) {
            if (record[variant] !== undefined && record[variant] !== null) {
                return record[variant];
            }
        }
        return '';
    }, []);

    const filteredData = useMemo(() => {
        let data = multiRowData;
        if (grouping && selectedGroup !== 'All') {
            data = data.filter(item => {
                if (item._isNew) return true;
                const val = item[grouping];
                if (Array.isArray(val)) {
                    return val.map(v => String(v)).includes(String(selectedGroup));
                }
                return String(val ?? '') === String(selectedGroup);
            });
        }
        if (headerSelectValue && headerSelectField) {
            data = data.filter(item => {
                if (item._isNew) return true;
                const val = item[headerSelectField];
                return String(val ?? '') === String(headerSelectValue);
            });
        }

        if (searchText) {
            const lowSearch = searchText.toLowerCase();
            data = data.filter(item => {
                if (item._isNew) return true;
                return fields.some(field => {
                    const variants = buildFieldNameVariants(field.name);
                    let val = '';
                    for (const v of variants) {
                        if (item[v] !== undefined && item[v] !== null && item[v] !== '') {
                            val = item[v];
                            break;
                        }
                    }

                    if (val === '') return false;

                    if (Array.isArray(val)) {
                        if (val.some(v => String(v).toLowerCase().includes(lowSearch))) return true;
                    } else if (String(val).toLowerCase().includes(lowSearch)) {
                        return true;
                    }
                    const displayVal = getDisplayValue(val, field, mergedDropdownData);
                    if (displayVal && String(displayVal).toLowerCase().includes(lowSearch)) return true;

                    return false;
                });
            });
        }

        return data;
    }, [multiRowData, grouping, selectedGroup, headerSelectValue, headerSelectField, searchText, fields, mergedDropdownData, getDisplayValue]);

    const gridRef = React.useRef(null);

    React.useEffect(() => {
        if (externalSearchText === undefined) {
            setInternalSearch('');
        }
    }, [multiRowData, externalSearchText]);

    React.useEffect(() => {
        const style = document.createElement('style');
        style.innerHTML = `
            .ag-theme-alpine.no-highlight {
                --ag-row-hover-color: transparent !important;
                --ag-selected-row-background-color: transparent !important;
                --ag-row-border-color: #E9ecef !important;
            }
            .ag-theme-alpine.no-highlight .ag-row-hover,
            .ag-theme-alpine.no-highlight .ag-row-selected,
            .ag-theme-alpine.no-highlight .ag-row-focus {
                background-color: transparent !important;
            }
            .ag-theme-alpine.no-highlight .ag-cell-focus,
            .ag-theme-alpine.no-highlight .ag-cell-no-focus {
                border: none !important;
                outline: none !important;
            }
            .no-highlight .ag-header-cell-label {
                justify-content: flex-start;
            }
            .no-highlight .center-header .ag-header-cell-label {
                justify-content: center;
            }
        `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    const noRowsTemplate = useMemo(() => {
        return `<div style="padding: 24px; text-align: center;">
            <div style="color: #6c757d; font-size: 0.9rem; font-weight: 500;">No records found</div>
        </div>`;
    }, [searchText]);


    const columnDefs = useMemo(() => {
        let headerFields = [];
        let specificHeaders = tableHeaderFields;
        if (typeof specificHeaders === 'string') {
            try {
                specificHeaders = JSON.parse(specificHeaders);
            } catch (e) {
                specificHeaders = [];
            }
        }

        const normField = (s) => (s || '').replace(/^DynamicCols\./i, '').toLowerCase().replace(/[_\s]/g, '');

        if (Array.isArray(specificHeaders) && specificHeaders.length > 0) {
            const findField = (fieldName) => {
                const exact = fields.find(f => f.name === fieldName);
                if (exact) return exact;
                const normName = normField(fieldName);
                return fields.find(f => normField(f.name) === normName)
                    || { name: fieldName, label: fieldName };
            };
            headerFields = specificHeaders.map(fieldName => findField(fieldName));
        } else {
            const visibleFields = fields.filter(f => !f.isHidden);
            headerFields = visibleFields;
        }
        const resolveValue = (data, fieldName) => {
            if (!data) return undefined;
            if (data[fieldName] !== undefined) return data[fieldName];
            const short = fieldName.replace(/^DynamicCols\./i, '');
            if (short !== fieldName && data[short] !== undefined) return data[short];
            const keys = Object.keys(data);
            const lower = fieldName.toLowerCase();
            const ciKey = keys.find(k => k.toLowerCase() === lower);
            if (ciKey !== undefined) return data[ciKey];
            if (short !== fieldName) {
                const shortLower = short.toLowerCase();
                const shortKey = keys.find(k => k.toLowerCase() === shortLower);
                if (shortKey !== undefined) return data[shortKey];
            }
            return undefined;
        };

        const defs = [];

        headerFields.forEach((field, index) => {
            defs.push({
                headerName: getDynFieldLabel(field),
                field: field.name,
                flex: 1,
                minWidth: 150,
                sortable: true,
                filter: true,
                valueGetter: (params) => resolveValue(params.data, field.name),
                cellRenderer: index === 0 ? 'agGroupCellRenderer' : (params) => getDisplayValue(params.value, field, mergedDropdownData),
                cellStyle: { display: 'flex', alignItems: 'center' },
                ...(index === 0 && {
                    cellRendererParams: {
                        innerRenderer: (params) => getDisplayValue(params.value, field, mergedDropdownData),
                        suppressCount: true
                    }
                })
            });
        });

        const canDelete = allowDelete && !readOnlyDialog && multiRowData.length > 1;
        if (canDelete) {
            defs.push({
                headerName: 'Actions',
                width: 100,
                pinned: 'right',
                suppressMovable: true,
                sortable: false,
                filter: false,
                headerClass: 'center-header',
                cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
                cellRenderer: (params) => (
                    <IconButton
                        size="small"
                        color="error"
                        sx={{ p: 0.5 }}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDeleteRow(params.data.id);
                        } }
                    >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                )
            });
        }

        return defs;
    }, [fields, allowDelete, readOnlyDialog, multiRowData.length, getDisplayValue, mergedDropdownData, onDeleteRow, tableHeaderFields]);

    const errorsRef = React.useRef(errors);
    const multiRowDataRef = React.useRef(multiRowData);
    const mergedDropdownDataRef = React.useRef(mergedDropdownData);

    React.useEffect(() => {
        errorsRef.current = errors;
        multiRowDataRef.current = multiRowData;
        mergedDropdownDataRef.current = mergedDropdownData;
    }, [errors, multiRowData, mergedDropdownData]);

    const onRowChangeRef = React.useRef(onRowChange);
    React.useEffect(() => {
        onRowChangeRef.current = onRowChange;
    }, [onRowChange]);

    const stableOnRowChange = React.useCallback((...args) => {
        if (onRowChangeRef.current) onRowChangeRef.current(...args);
    }, []);

    const detailCellRendererParams = useMemo(() => ({
        fields: fields.filter(f => !f.isHidden),
        onRowChange: stableOnRowChange,
        readOnlyDialog,
        getFieldValue,
        getOriginalValue,
        getDisplayValue,
        columns,
        getLatestErrors: () => errorsRef.current,
        getLatestMultiRowData: () => multiRowDataRef.current,
        getLatestMergedDropdownData: () => mergedDropdownDataRef.current,
    }), [fields, stableOnRowChange, readOnlyDialog, getFieldValue, getOriginalValue, getDisplayValue, columns]);

    const onGridReady = (params) => {
        gridRef.current = params;
        if (params.api.getDisplayedRowCount() === 0) {
            params.api.showNoRowsOverlay();
        } else {
            params.api.hideOverlay();
        }
    };

    const onFilterChanged = useCallback((params) => {
        if (params.api.getDisplayedRowCount() === 0) {
            params.api.showNoRowsOverlay();
        } else {
            params.api.hideOverlay();
        }
    }, []);

    React.useEffect(() => {
        if (gridRef.current?.api) {
            if (gridRef.current.api.getDisplayedRowCount() === 0) {
                gridRef.current.api.showNoRowsOverlay();
            } else {
                gridRef.current.api.hideOverlay();
            }
        }
    }, [multiRowData, searchText]);

    React.useEffect(() => {
        if (!gridRef.current?.api) return;

        if (Object.keys(errors).length > 0) {
            setTimeout(() => {
                if (!gridRef.current?.api) return;

                // Error keys are `{row.id}_{fieldName}` — extract row IDs as strings
                const rowsWithErrors = new Set();
                Object.keys(errors).forEach(errorKey => {
                    const underscoreIdx = errorKey.indexOf('_');
                    if (underscoreIdx !== -1) {
                        rowsWithErrors.add(errorKey.substring(0, underscoreIdx));
                    }
                });

                // For rows that have errors: collapse then re-expand so DetailRenderer
                // re-mounts and reads the updated errorsRef (fixes already-open rows).
                const nodesToReopen = [];
                gridRef.current.api.forEachNode((node) => {
                    if (node.data?.id !== undefined && node.data?.id !== null &&
                        rowsWithErrors.has(String(node.data.id))) {
                        nodesToReopen.push(node);
                    }
                });

                // Collapse all error rows first, then re-expand so DetailRenderer remounts
                nodesToReopen.forEach(node => node.setExpanded(false));
                requestAnimationFrame(() => {
                    nodesToReopen.forEach(node => node.setExpanded(true));
                });
            }, 100);
        }
    }, [errors]);

    return (
        <Box sx={{ mt: 2 }}>
            {showSearch && (
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {grouping && (
                            <TextField
                                select
                                size="small"
                                label="Grouping"
                                value={selectedGroup}
                                onChange={(e) => setSelectedGroup(e.target.value)}
                                sx={{
                                    width: 250,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#FFF',
                                        '&:hover': { backgroundColor: '#F1F3F5' },
                                        '& fieldset': { borderColor: '#E9ecef' },
                                    }
                                }}
                                InputProps={{
                                    endAdornment: selectedGroup !== 'All' && (
                                        <InputAdornment position="end" sx={{ position: 'absolute', right: 28 }}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedGroup('All');
                                                } }
                                            >
                                                <ClearIcon fontSize="small" sx={{ color: '#ADB5BD' }} />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            >
                                <MenuItem value="All">All {groupingField?.label || groupingField?.name || 'Groups'}</MenuItem>
                                {groupingOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                        {headerSelectOptions && headerSelectOptions.length > 0 && (
                            <TextField
                                select
                                size="small"
                                label={headerSelectLabel}
                                value={headerSelectValue || ''}
                                onChange={(e) => onHeaderSelectChange && onHeaderSelectChange(e.target.value)}
                                sx={{
                                    minWidth: 180,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: '#FFF',
                                        '&:hover': { backgroundColor: '#F1F3F5' },
                                        '& fieldset': { borderColor: '#E9ecef' },
                                    }
                                }}
                            >
                                {headerSelectOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}

                        <TextField
                            size="small"
                            placeholder="Search..."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            sx={{
                                width: 320,
                                '& .MuiOutlinedInput-root': {
                                    backgroundColor: '#FFF',
                                    '&:hover': { backgroundColor: '#F1F3F5' },
                                    '& fieldset': { borderColor: '#E9ecef' },
                                }
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" sx={{ color: '#ADB5BD' }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchText && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchText('')}
                                            edge="end"
                                            sx={{ color: '#ADB5BD' }}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }} />

                        {!readOnlyDialog && allowAddRow && (
                            <Button
                                onClick={() => onAddRow && onAddRow('top')}
                                size="small"
                                variant="outlined"
                                sx={{
                                    fontWeight: 500,
                                    display: 'flex',
                                    alignItems: 'center',
                                    py: 0.8,
                                    height: 40,
                                    textTransform: 'none',
                                    fontSize: '0.875rem',
                                    color: '#FF6600',
                                    borderColor: '#FF6600',
                                    '&:hover': { backgroundColor: '#FFF0E6', borderColor: '#e55a00' }
                                }}
                            >
                                <AddIcon sx={{ fontSize: 20 }} /> {actionLabels?.add || 'Add Row'}
                            </Button>
                        )}
                    </Box>
                </Box>
            )}

            <div className="ag-theme-alpine no-highlight" style={{ width: '100%', height: 'auto', border: '1px solid #E9ecef', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#fff' }}>
                <AgGridReact
                    onGridReady={onGridReady}
                    rowData={filteredData}
                    columnDefs={columnDefs}
                    domLayout="autoHeight"
                    defaultColDef={{
                        resizable: true,
                        suppressCellSelection: true
                    }}
                    masterDetail={true}
                    detailCellRenderer={DetailRenderer}
                    detailCellRendererParams={detailCellRendererParams}
                    detailRowAutoHeight={true}
                    overlayNoRowsTemplate={noRowsTemplate}
                    onFilterChanged={onFilterChanged}
                    headerHeight={40}
                    rowHeight={48}
                    suppressRowClickSelection={true}
                    suppressRowHoverHighlight={true}
                    reactiveCustomComponents={true}
                    getRowId={React.useCallback((params) => {
                        if (params.data.id) return String(params.data.id);
                        const idx = multiRowData.indexOf(params.data);
                        return idx !== -1 ? String(idx) : String(params.data.provider_Id || Math.random());
                    }, [multiRowData])} />
            </div>
        </Box>
    );
}

export default MasterDynamicDialog;
